import React from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ArrowRight,
  GoogleLogo,
  Envelope,
  ShieldCheck as PhShieldCheck,
} from "@phosphor-icons/react";
import {
  ClipboardList,
  Layers,
  FileCheck2,
  ShieldCheck,
  Lock,
  Sparkles,
  Scale as ScaleIcon,
  Wallet,
  BookOpen,
  Gauge,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Home,
  HandCoins,
  Briefcase,
  Users,
  Building2,
  HeartHandshake,
  ChevronDown,
} from "lucide-react";
import DisclaimerBanner from "../components/DisclaimerBanner";
import ProseLogo from "../components/ProseLogo";
import SiteFooter from "../components/SiteFooter";
import ProductTour from "../components/ProductTour";
import { toast } from "sonner";
import { track } from "../lib/analytics";

// ────────────────────────────────────────────────────────────────────────────
// PALETTE — token map kept inline for grep-ability. Every color used on this
// page must be listed here so a future redesign stays consistent.
// ────────────────────────────────────────────────────────────────────────────
const C = {
  navy: "#101C3A",
  navyDeep: "#0A1330",
  blue: "#2563EB",
  blueSoft: "#EFF6FF",
  coral: "#FF6B35",
  coralSoft: "#FFF7ED",
  teal: "#14B8A6",
  ink: "#172033",
  slate: "#475569",
  slateSoft: "#F1F5F9",
};

const handleSignIn = (source) => {
  track("lp_cta_click", { source: source || "unknown" });
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const handleEmailSignIn = () => {
  track("lp_cta_click", { source: "email_placeholder" });
  toast("Email login coming soon", { description: "Use Continue with Google for now." });
};

// ────────────────────────────────────────────────────────────────────────────
// Data
// ────────────────────────────────────────────────────────────────────────────

const HOW_STEPS = [
  {
    n: "01",
    icon: ClipboardList,
    title: "Tell Your Story",
    body:
      "Answer guided questions about what happened, who was involved, and when. Plain-English prompts, no legalese, no forms to hunt down.",
  },
  {
    n: "02",
    icon: Layers,
    title: "Build Your Claim",
    body:
      "Organize your facts, evidence, deadlines, parties, and the relief you're asking for — auto-labeled as Exhibit A, B, C… in a clean chronological view.",
  },
  {
    n: "03",
    icon: FileCheck2,
    title: "Prepare Your Documents",
    body:
      "Review the ClaimReady Report, generate a structured complaint draft, and export a court-ready PDF or DOCX for independent review.",
  },
];

// Every stat has an inline named source + year directly beneath the number.
// If we can't cite a specific study, we don't publish the number.
const STATS = [
  {
    value: "75%+",
    label: "of civil cases in state courts have at least one self-represented party.",
    sourceName: "Self-Represented Litigation Network, Brief #1 (2019 review)",
    href: "https://www.srln.org/node/548/srln-brief-how-many-srls-slrn-",
  },
  {
    value: "$1.4B",
    label: "in annual federal funding for civil legal aid across the U.S.",
    sourceName: "Legal Services Corporation FY2024 appropriation",
    href: "https://www.lsc.gov/",
  },
  {
    value: "92%",
    label: "of substantial civil legal problems in low-income households received no or inadequate legal help.",
    sourceName: "LSC Justice Gap Report (2022)",
    href: "https://www.lsc.gov/initiatives/justice-gap-research",
  },
];

const CLAIM_TYPES = [
  { icon: Home, title: "Housing & Eviction Defense" },
  { icon: HandCoins, title: "Consumer Debt & Small Claims" },
  { icon: Briefcase, title: "Employment & Wage Claims" },
  { icon: Users, title: "Family & Civil Matters" },
  { icon: Building2, title: "Landlord–Tenant Disputes" },
  { icon: HeartHandshake, title: "Public Benefits & Services" },
];

const FAQS = [
  {
    q: "Is ProSe.Claims a law firm?",
    a: "No. ProSe.Claims is a technology platform. It does not provide legal advice and does not create an attorney-client relationship. Every generated document is a draft that should be reviewed by a licensed attorney before filing.",
  },
  {
    q: "Is my information private?",
    a: "Yes. Your data is stored in your workspace only, protected by session-scoped authentication, and never sold. You can request full deletion any time from the Account page; there's a 30-day recovery window before permanent erasure.",
  },
  {
    q: "How much does it cost?",
    a: "Early access is currently free. If we introduce paid features in the future we'll notify you before any fees apply.",
  },
  {
    q: "What file types can I upload?",
    a: "PDF, DOCX, images (PNG/JPG/WEBP/HEIC), TXT, and RTF up to 15 MB each. Free workspaces get 100 MB total.",
  },
  {
    q: "Can I export what I create?",
    a: "Yes — every draft complaint, ClaimReady Report, and evidence inventory exports to PDF or DOCX so you or your attorney can file it.",
  },
];

// ────────────────────────────────────────────────────────────────────────────
// Product preview shown in the hero — mimics the actual case editor
// so visitors see the software, not a stock legal image.
// ────────────────────────────────────────────────────────────────────────────
function HeroProductPreview({ testid = "hero-product-preview" }) {
  return (
    <div
      className="relative rounded-2xl overflow-hidden border shadow-2xl"
      style={{
        background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
        borderColor: "rgba(255,255,255,0.08)",
        boxShadow: "0 40px 80px -30px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)",
      }}
      data-testid={testid}
    >
      {/* Traffic-light bar */}
      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b" style={{ borderColor: C.slateSoft }}>
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#FF605C" }} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#FFBD44" }} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#00CA4E" }} />
        <div className="ml-4 text-[10px] font-mono tracking-wider text-slate-500">
          prose.claims / matters / doe-v-acme
        </div>
      </div>

      <div className="grid grid-cols-3 gap-0">
        {/* Left: score ring */}
        <div
          className="col-span-1 p-6 flex flex-col items-center justify-center border-r"
          style={{ background: C.blueSoft, borderColor: C.slateSoft }}
        >
          <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: C.blue }}>
            ClaimReady Score
          </div>
          <div className="relative mt-3 w-[140px] h-[140px]">
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
              <circle cx="50" cy="50" r="42" strokeWidth="8" fill="none" stroke="#E2E8F0" />
              <circle
                cx="50" cy="50" r="42" strokeWidth="8" fill="none"
                stroke={C.teal}
                strokeDasharray={2 * Math.PI * 42}
                strokeDashoffset={2 * Math.PI * 42 * (1 - 0.82)}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="font-serif-display text-5xl font-semibold" style={{ color: C.navy }}>82</div>
              <div className="text-[10px] font-mono tracking-widest uppercase mt-0.5" style={{ color: C.teal }}>
                Ready for review
              </div>
            </div>
          </div>
          <div className="mt-4 text-[11px] text-slate-600 text-center">
            Higher scores mean fewer gaps between your evidence and your complaint.
          </div>
        </div>

        {/* Right: checklist + preview */}
        <div className="col-span-2 p-6">
          <div className="space-y-2.5">
            <ChecklistRow ok label="Facts completed" detail="14 events on timeline" />
            <ChecklistRow ok label="Evidence organized" detail="9 exhibits · A–I" />
            <ChecklistRow ok label="Parties confirmed" detail="Plaintiff · 2 defendants" />
            <ChecklistRow warn label="Add damages calculation" detail="Recommended before filing" />
            <ChecklistRow next label="Next: run ClaimReady Report" detail="≈ 2 minutes" />
          </div>

          <div className="mt-5 rounded-lg border p-3" style={{ borderColor: C.slateSoft, background: "#FFFFFF" }}>
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-3.5 h-3.5" style={{ color: C.blue }} />
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                Draft complaint · preview
              </div>
              <span className="ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded" style={{ background: C.coralSoft, color: C.coral }}>
                DRAFT
              </span>
            </div>
            <div className="font-serif-display text-[11px] leading-relaxed text-slate-700 space-y-0.5">
              <div className="font-semibold text-center text-[12px]" style={{ color: C.navy }}>
                UNITED STATES DISTRICT COURT
              </div>
              <div className="text-center text-slate-500 text-[10px]">Northern District of California</div>
              <div className="border-t mt-1.5 pt-1.5" style={{ borderColor: C.slateSoft }}>
                <span className="font-semibold">Doe,</span> Plaintiff,
                <br /> v. <span className="font-semibold">Acme Corp.</span>, Defendant.
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                I. Parties · II. Jurisdiction · III. Facts · IV. Claims · V. Prayer for Relief
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChecklistRow({ ok, warn, next, label, detail }) {
  const cfg = ok
    ? { color: C.teal, bg: "#F0FDFA", Icon: CheckCircle2 }
    : warn
    ? { color: C.coral, bg: C.coralSoft, Icon: AlertTriangle }
    : { color: C.blue, bg: C.blueSoft, Icon: Sparkles };
  const { Icon } = cfg;
  return (
    <div className="flex items-center gap-3 rounded-md px-3 py-2" style={{ background: cfg.bg }}>
      <Icon className="w-4 h-4 shrink-0" style={{ color: cfg.color }} />
      <div className="text-[12px] font-medium flex-1 truncate" style={{ color: C.ink }}>{label}</div>
      <div className="text-[11px] text-slate-500 tabular-nums">{detail}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────────────────────

export default function Landing() {
  const { user, loading } = useAuth();
  if (!loading && user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen" style={{ background: "#FFFFFF", color: C.ink }}>
      <DisclaimerBanner />

      {/* Top Nav */}
      <nav className="border-b" style={{ borderColor: C.slateSoft, background: "#FFFFFF" }}>
        <div className="max-w-[1400px] mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="landing-logo">
            <ProseLogo size={52} />
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm font-medium">
            <Link to="/rd-case-study" className="hover:opacity-70" style={{ color: C.slate }} data-testid="nav-case-study">
              How It Works
            </Link>
            <Link to="/contact" className="hover:opacity-70" style={{ color: C.slate }} data-testid="nav-contact">
              Contact
            </Link>
            <button
              onClick={() => handleSignIn("nav")}
              data-testid="nav-signin-btn"
              className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-lg text-white hover:opacity-90 transition-opacity"
              style={{ background: C.navy }}
            >
              Sign In <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{
          background: `linear-gradient(160deg, ${C.navy} 0%, ${C.navyDeep} 60%, #060B1F 100%)`,
        }}
        data-testid="hero-section"
      >
        {/* Subtle grid backdrop */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />
        {/* Coral glow */}
        <div
          aria-hidden="true"
          className="absolute -top-40 -right-24 w-[520px] h-[520px] rounded-full blur-3xl opacity-30 pointer-events-none"
          style={{ background: `radial-gradient(closest-side, ${C.coral} 0%, transparent 70%)` }}
        />
        {/* Blue glow */}
        <div
          aria-hidden="true"
          className="absolute -bottom-32 -left-20 w-[520px] h-[520px] rounded-full blur-3xl opacity-30 pointer-events-none"
          style={{ background: `radial-gradient(closest-side, ${C.blue} 0%, transparent 70%)` }}
        />

        <div className="relative max-w-[1400px] mx-auto px-6 pt-16 md:pt-24 pb-20 md:pb-28 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 lg:col-span-6">
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide"
              style={{ background: "rgba(255,107,53,0.15)", color: C.coral }}
              data-testid="hero-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: C.coral }} />
              Early Access · Currently Free
            </span>

            <h1
              className="mt-6 font-serif-display font-semibold tracking-tight text-white leading-[0.98]"
              style={{ fontSize: "clamp(2.75rem, 5.5vw, 4.75rem)" }}
            >
              Build Your Civil Complaint With{" "}
              <span style={{ color: C.coral }}>Clarity</span>.
            </h1>
            <div
              className="mt-4 font-serif-display font-medium italic"
              style={{ color: "#93C5FD", fontSize: "clamp(1.25rem, 2.2vw, 1.75rem)" }}
            >
              From what happened to a filing-ready draft.
            </div>

            <p className="mt-6 text-base md:text-lg leading-relaxed max-w-xl" style={{ color: "#CBD5E1" }}>
              ProSe.Claims guides you through your facts, evidence, deadlines, and documents one
              step at a time — with a readiness score, evidence organizer, and structured draft
              complaint you can hand to a licensed attorney for review.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <button
                onClick={() => handleSignIn("hero")}
                data-testid="hero-cta-btn"
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-lg text-white font-semibold text-[15px] transition-all hover:-translate-y-0.5"
                style={{ background: C.coral, boxShadow: "0 12px 28px -12px rgba(255,107,53,0.55)" }}
              >
                Start My Claim
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <a
                href="#how-it-works"
                onClick={() => track("lp_cta_click", { source: "hero_secondary" })}
                data-testid="hero-secondary-btn"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-lg font-semibold text-[15px] text-white/90 border transition-colors hover:bg-white/5"
                style={{ borderColor: "rgba(255,255,255,0.22)" }}
              >
                See How It Works
              </a>
            </div>

            {/* Trust chips */}
            <div className="mt-10 flex flex-wrap items-center gap-4 text-[13px]" style={{ color: "#94A3B8" }}>
              <TrustChip icon={PhShieldCheck} label="Encrypted workspace" />
              <TrustChip icon={CheckCircle2} label="Guided preparation" />
              <TrustChip icon={AlertTriangle} label="Not legal advice" />
            </div>
          </div>

          <div className="col-span-12 lg:col-span-6">
            <ProductTour testid="hero-product-preview" />
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-y" style={{ background: C.slateSoft, borderColor: "#E2E8F0" }}>
        <div className="max-w-[1400px] mx-auto px-6 py-6 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10 text-sm">
          <TrustLine icon={Lock} title="Secure Workspace" body="MFA, session-scoped auth, evidence stored in Emergent Object Storage." />
          <TrustLine icon={Gauge} title="Guided Preparation" body="Numeric ClaimReady Score plus a plain-English readiness label." />
          <TrustLine icon={AlertTriangle} title="Not a Law Firm" body="ProSe.Claims is a technology platform. Attorney review is required before filing." />
        </div>
      </section>

      {/* How it works — three big cards */}
      <section id="how-it-works" className="py-24 md:py-28" style={{ background: "#FFFFFF" }}>
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="max-w-3xl">
            <div className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: C.blue }}>
              How ProSe.Claims Works
            </div>
            <h2
              className="mt-3 font-serif-display font-semibold tracking-tight leading-tight"
              style={{ color: C.navy, fontSize: "clamp(2rem, 4vw, 3rem)" }}
            >
              Three steps from raw facts to filing-ready draft.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {HOW_STEPS.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.n}
                  data-testid={`how-card-${s.n}`}
                  className="group relative rounded-2xl p-8 md:p-10 border transition-all hover:-translate-y-1"
                  style={{
                    background: "#FFFFFF",
                    borderColor: "#E2E8F0",
                    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 24px 48px -24px rgba(37,99,235,0.25)")}
                  onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 1px 2px rgba(15,23,42,0.04)")}
                >
                  <div className="flex items-center justify-between mb-6">
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center transition-colors"
                      style={{ background: C.blueSoft }}
                    >
                      <Icon className="w-7 h-7" style={{ color: C.blue }} strokeWidth={2} />
                    </div>
                    <div className="font-mono text-sm font-semibold" style={{ color: "#CBD5E1" }}>
                      {s.n}
                    </div>
                  </div>
                  <h3 className="font-serif-display text-2xl md:text-[26px] font-semibold leading-tight" style={{ color: C.navy }}>
                    {s.title}
                  </h3>
                  <p className="mt-3 text-[15px] leading-relaxed" style={{ color: C.slate }}>
                    {s.body}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-12">
            <Link
              to="/rd-case-study"
              className="inline-flex items-center gap-1.5 font-semibold hover:gap-2 transition-all"
              style={{ color: C.blue }}
              data-testid="how-learn-more"
            >
              Read the full methodology <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ClaimReady Score deep-dive */}
      <section className="py-24 md:py-28" style={{ background: C.slateSoft }}>
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: C.teal }}>
              ClaimReady Score
            </div>
            <h2
              className="mt-3 font-serif-display font-semibold tracking-tight leading-tight"
              style={{ color: C.navy, fontSize: "clamp(2rem, 4vw, 2.75rem)" }}
            >
              Know exactly what&apos;s missing before you file.
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed max-w-xl" style={{ color: C.slate }}>
              Every matter gets a live 0–100 readiness score plus a readiness label —
              <em> Not Started · Building · Organized · Ready for Review</em>. The score
              tracks parties, timeline, evidence, damages, jurisdiction, and citations, so
              you always see the gap between where your case is and where it needs to be.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-4">
              <MiniStat label="Categories checked" value="12" color={C.blue} />
              <MiniStat label="Deterministic formula" value="v3" color={C.teal} />
              <MiniStat label="Free-tier storage" value="100 MB" color={C.coral} />
              <MiniStat label="Export formats" value="PDF · DOCX" color={C.navy} />
            </div>

            <button
              onClick={() => handleSignIn("score_section")}
              data-testid="score-cta-btn"
              className="mt-10 inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-white font-semibold text-sm hover:opacity-90 transition"
              style={{ background: C.navy }}
            >
              Start Free <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="relative">
            <div
              className="absolute -inset-6 rounded-3xl opacity-40 blur-2xl"
              style={{ background: `linear-gradient(135deg, ${C.teal}, ${C.blue})` }}
            />
            <div
              className="relative rounded-2xl border p-6 md:p-8"
              style={{ background: "#FFFFFF", borderColor: "#E2E8F0", boxShadow: "0 30px 60px -30px rgba(15,23,42,0.35)" }}
            >
              <ProductTour testid="score-section-preview" />
            </div>
          </div>
        </div>
      </section>

      {/* Supported claim categories */}
      <section className="py-24" style={{ background: "#FFFFFF" }}>
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="max-w-3xl">
            <div className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: C.coral }}>
              Supported Claim Categories
            </div>
            <h2
              className="mt-3 font-serif-display font-semibold tracking-tight leading-tight"
              style={{ color: C.navy, fontSize: "clamp(2rem, 4vw, 2.75rem)" }}
            >
              Prepare complaints for the disputes that most affect households.
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4">
            {CLAIM_TYPES.map((ct) => {
              const Icon = ct.icon;
              return (
                <div
                  key={ct.title}
                  className="rounded-xl p-6 border transition-all hover:-translate-y-0.5"
                  style={{ background: "#FFFFFF", borderColor: "#E2E8F0" }}
                  data-testid={`claim-cat-${ct.title.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center mb-4"
                    style={{ background: C.coralSoft }}
                  >
                    <Icon className="w-5 h-5" style={{ color: C.coral }} />
                  </div>
                  <div className="font-semibold text-[15px] leading-snug" style={{ color: C.navy }}>
                    {ct.title}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Feature deep-dive: evidence + resources */}
      <section className="py-24" style={{ background: C.blueSoft }}>
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <FeatureBlock
            eyebrow="Evidence Organizer"
            eyebrowColor={C.blue}
            title="Every exhibit auto-labeled, every event on the timeline."
            body="Drop in PDFs, receipts, screenshots, correspondence. We label them Exhibit A, B, C… and link each one to a moment on the timeline. Your evidence inventory exports as its own filing-ready PDF."
            bullets={[
              "PDF, DOCX, image, TXT, RTF uploads up to 15 MB",
              "Evidence inventory + timeline export",
              "Encrypted Emergent Object Storage",
            ]}
          />
          <FeatureBlock
            eyebrow="Deadline Tracking"
            eyebrowColor={C.teal}
            title="Hearing reminders, mandated warnings, and judge insights."
            body="Add court dates and we compute 7-day / 24-hour / 2-hour reminder chips, show the mandated pro-se warning banner, and surface anonymized courtroom stats for the judge + courthouse when enough data is available."
            bullets={[
              "Reminder chips for every hearing",
              "Mandated pro-se warning banner",
              "Anonymized judge / courthouse insights",
            ]}
          />
          <FeatureBlock
            eyebrow="Fee Waivers & Free Legal Help"
            eyebrowColor={C.coral}
            title="If you can't afford filing fees or a lawyer, start here."
            body="Federal + all 50 states + D.C. fee-waiver and in-forma-pauperis pages, plus the primary LSC-grantee legal-aid organization in every state with click-to-call intake hotlines."
            bullets={[
              "51 fee-waiver / IFP directories",
              "52 free legal-help entries with hotlines",
              "Focus areas: housing, family, benefits, consumer",
            ]}
            action={{ label: "Explore free legal help", to: "/rd-case-study" }}
          />
          <FeatureBlock
            eyebrow="Complaint Preparation"
            eyebrowColor={C.navy}
            title="Structured drafts with citation safety rails."
            body="Generate Facts, Causes of Action, and Prayer for Relief from your intake. Any statute or case citation not in your pinned Citations panel is automatically redacted before it reaches your draft."
            bullets={[
              "GPT-5.2 drafting, section-by-section",
              "Citation safety rails (USC, CFR, FRCP, state codes)",
              "PDF + DOCX export, ready for attorney review",
            ]}
          />
        </div>
      </section>

      {/* Statistics band — every number has a named source directly beneath */}
      <section className="py-24" style={{ background: "#FFFFFF" }} data-testid="stats-section">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="max-w-3xl">
            <div className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: C.blue }}>
              Why This Matters
            </div>
            <h2
              className="mt-3 font-serif-display font-semibold tracking-tight leading-tight"
              style={{ color: C.navy, fontSize: "clamp(2rem, 4vw, 2.75rem)" }}
            >
              The civil justice gap, in numbers you can verify.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {STATS.map((s, i) => (
              <div
                key={i}
                className="rounded-2xl border p-8"
                style={{ background: "#FFFFFF", borderColor: "#E2E8F0" }}
                data-testid={`stat-${i + 1}`}
              >
                <div className="font-serif-display font-semibold leading-none" style={{ color: C.coral, fontSize: "clamp(3rem, 6vw, 4.5rem)" }}>
                  {s.value}
                </div>
                <div className="mt-4 text-[15px] leading-relaxed" style={{ color: C.ink }}>
                  {s.label}
                </div>
                <a
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-1 text-xs font-medium underline underline-offset-4 hover:opacity-80"
                  style={{ color: C.slate }}
                  data-testid={`stat-source-${i + 1}`}
                >
                  Source: {s.sourceName} ↗
                </a>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs max-w-3xl leading-relaxed" style={{ color: C.slate }}>
            Every figure above is drawn from a specific, cited publication. If the underlying
            source ever becomes unavailable we&apos;ll remove the number from this page rather than
            leave it uncited.
          </p>
        </div>
      </section>

      {/* Security section */}
      <section className="py-24" style={{ background: C.navy }} data-testid="security-section">
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: C.coral }}>
              Security &amp; Privacy
            </div>
            <h2 className="mt-3 font-serif-display font-semibold tracking-tight leading-tight text-white" style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }}>
              Built for sensitive legal work from day one.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed" style={{ color: "#CBD5E1" }}>
              Your workspace is yours. We never sell your data, and you can request full
              deletion any time. Signature features:
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { icon: Lock, title: "MFA account protection", body: "TOTP-based multi-factor authentication with recovery codes." },
              { icon: PhShieldCheck, title: "Encrypted uploads", body: "Files stored via Emergent Object Storage, scoped to your account." },
              { icon: BookOpen, title: "Right to delete", body: "Soft-delete with 30-day recovery window, then permanent erasure." },
              { icon: ScaleIcon, title: "Consent-per-matter", body: "Every new matter requires an explicit not-legal-advice acknowledgement." },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  className="rounded-xl p-5 border"
                  style={{ background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.08)" }}
                >
                  <Icon className="w-5 h-5 mb-3" style={{ color: C.teal }} />
                  <div className="text-white font-semibold text-[15px] mb-1">{s.title}</div>
                  <div className="text-[13px]" style={{ color: "#CBD5E1" }}>{s.body}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24" style={{ background: "#FFFFFF" }} data-testid="faq-section">
        <div className="max-w-[900px] mx-auto px-6">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: C.blue }}>
              Frequently Asked
            </div>
            <h2 className="mt-3 font-serif-display font-semibold tracking-tight leading-tight" style={{ color: C.navy, fontSize: "clamp(2rem, 4vw, 2.75rem)" }}>
              Questions from other pro-se filers.
            </h2>
          </div>
          <div className="mt-10 divide-y" style={{ borderColor: "#E2E8F0" }}>
            {FAQS.map((f, i) => (
              <details key={i} className="group py-5" data-testid={`faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="font-serif-display text-xl font-semibold pr-4" style={{ color: C.navy }}>
                    {f.q}
                  </span>
                  <ChevronDown className="w-5 h-5 shrink-0 transition-transform group-open:rotate-180" style={{ color: C.slate }} />
                </summary>
                <p className="mt-3 text-[15px] leading-relaxed" style={{ color: C.slate }}>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Early access CTA (replaces R&D wording) */}
      <section className="py-24" style={{ background: `linear-gradient(135deg, ${C.coral} 0%, #F4491D 100%)` }} data-testid="cta-band">
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <h2
            className="font-serif-display font-semibold tracking-tight text-white leading-[1.05]"
            style={{ fontSize: "clamp(2.25rem, 5vw, 3.5rem)" }}
          >
            Early access is <em className="not-italic underline decoration-white/40 decoration-4 underline-offset-[6px]">currently free</em>.
          </h2>
          <p className="mt-5 text-white/90 max-w-2xl mx-auto text-[15px] md:text-base leading-relaxed">
            Help us improve ProSe.Claims while preparing and organizing your claim at no cost.
            Sign in, create your first matter, and export a draft in a single session.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => handleSignIn("final_cta")}
              data-testid="final-cta-btn"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg text-[15px] font-semibold hover:-translate-y-0.5 transition-all"
              style={{ background: "#FFFFFF", color: C.navy, boxShadow: "0 12px 28px -12px rgba(0,0,0,0.35)" }}
            >
              Start Free <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={handleEmailSignIn}
              data-testid="final-email-btn"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg text-[15px] font-semibold text-white border transition-colors hover:bg-white/10"
              style={{ borderColor: "rgba(255,255,255,0.5)" }}
            >
              <Envelope className="w-4 h-4" /> Continue with Email
            </button>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Small primitives kept at the bottom to keep the top of the file scannable.
// ────────────────────────────────────────────────────────────────────────────

function TrustChip({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </span>
  );
}

function TrustLine({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <div className="shrink-0 mt-0.5 w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#FFFFFF" }}>
        <Icon className="w-4 h-4" style={{ color: C.blue }} />
      </div>
      <div>
        <div className="font-semibold" style={{ color: C.navy }}>{title}</div>
        <div className="text-slate-600 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div className="rounded-xl bg-white border p-4" style={{ borderColor: "#E2E8F0" }}>
      <div className="text-[11px] font-mono uppercase tracking-widest" style={{ color: C.slate }}>{label}</div>
      <div className="mt-1 font-serif-display text-2xl font-semibold" style={{ color }}>{value}</div>
    </div>
  );
}

function FeatureBlock({ eyebrow, eyebrowColor, title, body, bullets, action }) {
  return (
    <div className="rounded-2xl bg-white border p-8 md:p-10" style={{ borderColor: "#E2E8F0" }} data-testid={`feature-block-${eyebrow.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: eyebrowColor }}>{eyebrow}</div>
      <h3 className="mt-2 font-serif-display text-2xl md:text-[26px] font-semibold leading-tight" style={{ color: C.navy }}>
        {title}
      </h3>
      <p className="mt-3 text-[15px] leading-relaxed" style={{ color: C.slate }}>{body}</p>
      <ul className="mt-5 space-y-2 text-[14px]">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2" style={{ color: C.ink }}>
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.teal }} />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      {action && (
        <Link to={action.to} className="mt-6 inline-flex items-center gap-1.5 font-semibold hover:gap-2 transition-all" style={{ color: C.blue }}>
          {action.label} <ArrowRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
}
